import { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { fetchMisCompanyProfile, saveMisCompanyProfile, actions } from '../../../store/store';

// Phase 6 — companyProfile settings editor (Session 45, admin / mis:settings:edit).
// The STATIC invoice template header/footer: seller identity + TRN, bank block,
// VAT default, thank-you note, quote validity. NEVER hardcoded in the template —
// this editor is the single place these values are maintained.

const SECTIONS = [
  {
    titleKey: 'mis.settingsSellerIdentity',
    fields: [
      { key: 'nameAr',          labelKey: 'mis.fieldCompanyNameAr', rtl: true },
      { key: 'nameEn',          labelKey: 'mis.fieldCompanyNameEn' },
      { key: 'trn',             labelKey: 'mis.fieldTrn' },
      { key: 'branchAddressAr', labelKey: 'mis.fieldBranchAddressAr', rtl: true },
      { key: 'phonesText',      labelKey: 'mis.fieldPhones' },
      { key: 'email',           labelKey: 'mis.fieldEmail' },
      { key: 'website',         labelKey: 'mis.fieldWebsite' },
    ],
  },
  {
    titleKey: 'mis.settingsBankDetails',
    fields: [
      { key: 'bank.name',          labelKey: 'mis.fieldBankName' },
      { key: 'bank.accountNumber', labelKey: 'mis.fieldAccountNumber' },
      { key: 'bank.iban',          labelKey: 'mis.fieldIban' },
      { key: 'bank.branch',        labelKey: 'mis.fieldBankBranch' },
      { key: 'bank.swift',         labelKey: 'mis.fieldSwift' },
    ],
  },
  {
    titleKey: 'mis.settingsDocDefaults',
    fields: [
      { key: 'vatRate',                      labelKey: 'mis.fieldVatRate', type: 'number' },
      { key: 'quotationValidityDefaultDays', labelKey: 'mis.fieldQuoteValidityDays', type: 'number' },
      { key: 'thankYouNoteAr',               labelKey: 'mis.fieldThankYouNoteAr', rtl: true, multiline: true },
    ],
  },
];

const getPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
const setPath = (obj, path, value) => {
  const keys = path.split('.');
  const next = { ...obj };
  let cur = next;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) cur[k] = value;
    else { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; }
  });
  return next;
};

export default function CompanyProfileDrawer({ open, onClose }) {
  const { t } = useTranslation();
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch    = useDispatch();
  const authCtx     = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { activeBranchId, branches } = useBranch();
  const branchName = ((branches || []).find((b) => String(b._id) === String(activeBranchId)) || {}).name || '';

  const profile = useSelector(s => s.misCompanyProfile);
  const [loading, setLoading] = useState(false);

  const T = {
    PANEL_BG: isDark ? '#0d0d0d' : theme.palette.background.paper,
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
  };

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Opening it, or switching branch while it is open, ALWAYS starts from a clean
  // slate: the previous branch's settings are cleared first, so what's on screen
  // is only ever the branch it names in the header.
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true);
    setValues({});
    dispatch(actions.misSetCompanyProfile(null));
    Promise.resolve(dispatch(fetchMisCompanyProfile({ authCtx, axiosGlobal, branchId: activeBranchId })))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeBranchId]);

  useEffect(() => {
    if (profile) {
      setValues({ ...profile, phonesText: (profile.phones || []).join(', ') });
    }
  }, [profile]);

  // A branch with no settings of its own is shown the shared defaults; saving
  // is what gives it its own.
  const inherited = Boolean(profile) && !profile.branchId;

  const handleSave = async () => {
    setSaving(true);
    const { phonesText, _id, key, branchId, updateDate, updatedBy, __v, ...rest } = values;
    const data = {
      ...rest,
      phones: (phonesText || '').split(',').map(p => p.trim()).filter(Boolean),
      vatRate: Number(values.vatRate) || 0,
      quotationValidityDefaultDays: Number(values.quotationValidityDefaultDays) || 0,
      // Session 72 — editing while a branch is active saves THAT branch's own
      // override; the settings screen has no "edit the global fallback"
      // affordance today, matching how every other MIS/Inventory screen is
      // always scoped to the active branch.
      branchId: activeBranchId,
    };
    await dispatch(saveMisCompanyProfile({ authCtx, axiosGlobal, data }));
    setSaving(false);
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 400 },
        bgcolor: T.PANEL_BG, borderLeft: `1px solid ${T.BD}` } }}>

      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5,
        borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: T.TEXT_PRI }}>
            {t('mis.templateSettingsTitle')}
          </Typography>
          {branchName && (
            <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }} noWrap>
              {t('mis.settingsForBranch', { branch: branchName })}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: T.TEXT_TER }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2,
        display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        )}
        {!loading && !profile && (
          <Typography sx={{ fontSize: '0.78rem', color: T.TEXT_SEC, textAlign: 'center', py: 4 }}>
            {t('mis.settingsLoadFailed')}
          </Typography>
        )}
        {!loading && inherited && (
          <Box sx={{ px: 1.25, py: 1, borderRadius: '9px', border: `1px solid ${T.BD}` }}>
            <Typography sx={{ fontSize: '0.74rem', color: T.TEXT_SEC }}>
              {t('mis.settingsInherited', { branch: branchName })}
            </Typography>
          </Box>
        )}
        {!loading && profile && SECTIONS.map(section => (
          <Box key={section.titleKey}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1,
              textTransform: 'uppercase', color: T.TEXT_TER, mb: 1.25 }}>
              {t(section.titleKey)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {section.fields.map(f => (
                <TextField key={f.key} size="small" fullWidth
                  label={t(f.labelKey)}
                  type={f.type || 'text'}
                  multiline={Boolean(f.multiline)}
                  minRows={f.multiline ? 2 : undefined}
                  value={getPath(values, f.key) ?? ''}
                  onChange={(e) => setValues(v => setPath(v, f.key, e.target.value))}
                  inputProps={{ style: { fontSize: '0.8rem', direction: f.rtl ? 'rtl' : 'ltr' } }}
                  InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: T.BD } }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${T.BD}`, display: 'flex', gap: 1, flexShrink: 0 }}>
        <Button fullWidth variant="contained" size="small" disabled={saving || loading || !profile} onClick={handleSave}
          sx={{ fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
          {saving ? <CircularProgress size={14} sx={{ mr: 0.75 }} /> : null}
          {t('mis.saveSettings')}
        </Button>
      </Box>
    </Drawer>
  );
}
